import React, { useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { Box, Button, TextField, Typography } from "@material-ui/core";
import { Send } from "@material-ui/icons";
import { i18n } from "../../../translate/i18n";

/**
 * Resolución del punto de pausa (0383 FS-2.6).
 *
 * En una conversación real esta elección la hace el cliente; aquí la toma quien está diseñando
 * el flujo. Los `value` de las opciones son los `sourceHandle` reales del grafo, así que la
 * rama que se recorra es exactamente la que recorrería producción.
 */
const SimPrompt = ({ awaiting, disabled, onReply }) => {
  const theme = useTheme();
  const [text, setText] = useState("");

  if (!awaiting) return null;

  const submitText = () => {
    if (!text.trim()) return;
    onReply({ text });
    setText("");
  };

  const isChoice = awaiting.kind === "menu" || awaiting.kind === "buttons";
  const options = awaiting.options || [];

  return (
    <Box
      style={{
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.25),
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography
        variant="caption"
        display="block"
        style={{ color: theme.palette.text.secondary, marginBottom: 8 }}
      >
        {isChoice
          ? i18n.t("flowbuilders.simulator.chooseOption") ||
            "Selecciona una opción para continuar. En conversaciones reales la elección la hace el cliente."
          : i18n.t("flowbuilders.simulator.typeReply") ||
            "Escribe la respuesta del cliente para continuar."}
      </Typography>

      {isChoice && (
        <Box display="flex" flexDirection="column" style={{ gap: 6, marginBottom: 10 }}>
          {options.map((opt, index) => (
            <Button
              key={`${opt.value}-${index}`}
              variant="outlined"
              size="small"
              fullWidth
              disabled={disabled}
              onClick={() =>
                onReply(
                  awaiting.kind === "menu"
                    ? { optionIndex: index, text: String(index + 1) }
                    : { buttonId: opt.value }
                )
              }
            >
              {awaiting.kind === "menu" ? `${index + 1}. ${opt.label}` : opt.label}
            </Button>
          ))}
          {options.length === 0 && (
            <Typography variant="caption" color="error">
              {i18n.t("flowbuilders.simulator.noOptions") ||
                "Este nodo no tiene opciones configuradas: el flujo quedaría bloqueado aquí."}
            </Typography>
          )}
        </Box>
      )}

      {/* La caja de texto está SIEMPRE disponible, también en menús y botones: en una
          conversación real el cliente puede escribir cualquier cosa —incluido un número que no
          existe— y esa rama de error hay que poder probarla. Los botones de arriba son el
          atajo para la respuesta correcta, no la única entrada posible. */}
      <Box display="flex" style={{ gap: 6 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={text}
          disabled={disabled}
          placeholder={
            isChoice
              ? i18n.t("flowbuilders.simulator.freeTextPlaceholder") ||
                "…o escribe cualquier respuesta (p. ej. 9)"
              : i18n.t("flowbuilders.simulator.replyPlaceholder") || "Respuesta del cliente…"
          }
          onChange={(e) => setText(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter") submitText();
          }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={disabled || !text.trim()}
          onClick={submitText}
        >
          <Send style={{ fontSize: 18 }} />
        </Button>
      </Box>
    </Box>
  );
};

export default SimPrompt;
